import React from "react";
import { Link } from "react-router-dom";
import LabelMockup from "@/components/LabelMockup";

/**
 * Ultra-light Markdown renderer for blog posts.
 * Supports:
 *   ##  H2 (with anchor id)
 *   -   list items
 *   >   callout / checklist block
 *   **bold**, *italic*, [text](url)
 *   ---  horizontal rule
 *   blank line → new <p>
 */

const slugify = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// Inline formatter: **bold**, *italic*, [text](url), backtick code
function renderInline(text, keyPrefix) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let m;
  let idx = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const token = m[0];
    const key = `${keyPrefix}-${idx++}`;
    if (token.startsWith("**")) {
      parts.push(
        <strong key={key} className="text-stone-900 font-black">
          {token.slice(2, -2)}
        </strong>
      );
    } else if (token.startsWith("`")) {
      parts.push(
        <code
          key={key}
          className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 text-[0.9em] font-mono"
        >
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith("*")) {
      parts.push(
        <em key={key} className="italic">
          {token.slice(1, -1)}
        </em>
      );
    } else {
      const mm = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (mm) {
        const [, label, url] = mm;
        const internal = url.startsWith("/");
        parts.push(
          internal ? (
            <Link
              key={key}
              to={url}
              className="text-orange-600 font-bold underline decoration-orange-300 underline-offset-2 hover:decoration-orange-500"
            >
              {label}
            </Link>
          ) : (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-600 font-bold underline decoration-orange-300 underline-offset-2 hover:decoration-orange-500"
            >
              {label}
            </a>
          )
        );
      }
    }
    lastIndex = m.index + token.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

export default function MarkdownLite({ source }) {
  const lines = source.split("\n");
  const blocks = [];
  let listBuffer = [];
  let calloutBuffer = [];
  let paragraphBuffer = [];

  const flushList = () => {
    if (listBuffer.length) {
      blocks.push(
        <ul
          key={`ul-${blocks.length}`}
          className="my-5 space-y-2 pl-5 list-disc marker:text-orange-400 text-stone-700 leading-relaxed"
        >
          {listBuffer.map((li, i) => (
            <li key={`${i}-${li}`}>{renderInline(li, `li-${blocks.length}-${i}`)}</li>
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };
  const flushCallout = () => {
    if (calloutBuffer.length) {
      blocks.push(
        <div
          key={`co-${blocks.length}`}
          className="my-7 rounded-2xl bg-gradient-to-br from-orange-50 to-rose-50 border-l-4 border-orange-400 p-5 text-stone-700 leading-relaxed"
        >
          {calloutBuffer.map((ln, i) => (
            <p key={`${i}-${ln}`} className={i > 0 ? "mt-1.5" : ""}>
              {renderInline(ln, `co-${blocks.length}-${i}`)}
            </p>
          ))}
        </div>
      );
      calloutBuffer = [];
    }
  };
  const flushParagraph = () => {
    if (paragraphBuffer.length) {
      const text = paragraphBuffer.join(" ").trim();
      if (text) {
        blocks.push(
          <p
            key={`p-${blocks.length}`}
            className="my-4 text-stone-700 leading-relaxed"
          >
            {renderInline(text, `p-${blocks.length}`)}
          </p>
        );
      }
      paragraphBuffer = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("## ")) {
      flushList();
      flushCallout();
      flushParagraph();
      const title = line.slice(3).trim();
      const id = slugify(title);
      blocks.push(
        <h2
          key={`h2-${blocks.length}`}
          id={id}
          className="font-display text-2xl sm:text-3xl font-black text-stone-800 mt-12 mb-4 scroll-mt-24"
        >
          <span className="text-orange-500">#</span> {title}
        </h2>
      );
      continue;
    }
    if (line.startsWith("- ")) {
      flushCallout();
      flushParagraph();
      listBuffer.push(line.slice(2));
      continue;
    }
    if (line.startsWith("> ")) {
      flushList();
      flushParagraph();
      calloutBuffer.push(line.slice(2));
      continue;
    }
    if (line === "[[etiquette]]") {
      flushList();
      flushCallout();
      flushParagraph();
      blocks.push(<LabelMockup key={`lbl-${blocks.length}`} />);
      continue;
    }
    if (line.startsWith("---")) {
      flushList();
      flushCallout();
      flushParagraph();
      blocks.push(
        <hr
          key={`hr-${blocks.length}`}
          className="my-10 border-0 h-px bg-gradient-to-r from-transparent via-orange-200 to-transparent"
        />
      );
      continue;
    }
    if (line === "") {
      flushList();
      flushCallout();
      flushParagraph();
      continue;
    }
    paragraphBuffer.push(line);
  }
  flushList();
  flushCallout();
  flushParagraph();

  return <div className="max-w-none">{blocks}</div>;
}

export { slugify };
